import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ApiError, apiFetch } from '../../lib/api';
import type { ProductDetail, ProductListItem } from '../product/types';
import { buildOrderBody, emptyOrderLine, orderLinesValid, type OrderFormLine } from './order-form';

/** 订单创建页（F6 首步）：产品 + 数量 + 单位若干行 → DRAFT。 */
export function OrderCreatePage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [lines, setLines] = useState<OrderFormLine[]>([emptyOrderLine()]);
  const [requiredDate, setRequiredDate] = useState('');
  const [customerRefText, setCustomerRefText] = useState('');
  const [notes, setNotes] = useState('');
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const updateLine = (key: string, patch: Partial<OrderFormLine>): void =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const mutation = useMutation({
    mutationFn: () =>
      apiFetch<{ id: string; docNo: string }>('/orders', {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
        body: buildOrderBody({ requiredDate, customerRefText, notes }, lines),
      }),
    onSuccess: (result) => navigate(`/orders/${result.id}`),
  });

  const canSubmit = orderLinesValid(lines) && !mutation.isPending;

  return (
    <div style={{ maxWidth: '720px' }}>
      <h1 style={{ fontSize: 'var(--text-2xl)', marginBottom: 'var(--space-4)' }}>
        {t('orders.createTitle')}
      </h1>

      <div className="row" style={{ gap: 'var(--space-3)' }}>
        <label className="field" style={{ flex: 1 }}>
          <span>{t('orders.customerRefLabel')}</span>
          <input value={customerRefText} onChange={(e) => setCustomerRefText(e.target.value)} />
        </label>
        <label className="field" style={{ maxWidth: '200px' }}>
          <span>{t('orders.requiredDateLabel')}</span>
          <input
            type="date"
            value={requiredDate}
            onChange={(e) => setRequiredDate(e.target.value)}
          />
        </label>
      </div>
      <label className="field">
        <span>{t('orders.notesLabel')}</span>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
      </label>

      {lines.map((line, i) => (
        <OrderLineEditor
          key={line.key}
          line={line}
          index={i}
          onChange={(patch) => updateLine(line.key, patch)}
          onRemove={
            lines.length > 1
              ? () => setLines((ls) => ls.filter((l) => l.key !== line.key))
              : undefined
          }
        />
      ))}
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={() => setLines((ls) => [...ls, emptyOrderLine()])}
        style={{ margin: 'var(--space-2) 0' }}
      >
        {t('orders.addLine')}
      </button>

      {mutation.error instanceof ApiError && (
        <p className="field-error">
          {t(`errors:${mutation.error.code}`, {
            defaultValue: mutation.error.code,
            ...mutation.error.params,
          })}
        </p>
      )}

      <div className="row" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-4)' }}>
        <button className="btn btn--ghost" onClick={() => navigate('/orders')}>
          {t('app.cancel')}
        </button>
        <button
          className="btn btn--primary"
          disabled={!canSubmit}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? t('app.loading') : t('orders.createSubmit')}
        </button>
      </div>
    </div>
  );
}

function OrderLineEditor({
  line,
  index,
  onChange,
  onRemove,
}: {
  readonly line: OrderFormLine;
  readonly index: number;
  readonly onChange: (patch: Partial<OrderFormLine>) => void;
  readonly onRemove?: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const { data: options } = useQuery({
    queryKey: ['orders', 'product-search', search],
    queryFn: () =>
      apiFetch<{ items: ProductListItem[] }>(
        `/products?limit=8&query=${encodeURIComponent(search)}`,
      ),
    enabled: search.trim().length > 0 && line.productId === null,
  });
  const { data: product } = useQuery({
    queryKey: ['orders', 'product', line.productId],
    queryFn: () => apiFetch<ProductDetail>(`/products/${line.productId}`),
    enabled: line.productId !== null,
  });
  const activeUnits = useMemo(() => (product?.units ?? []).filter((u) => !u.archivedAt), [product]);

  return (
    <div className="card" style={{ padding: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
      <div className="row row--between">
        <strong>{t('transfer.lineLabel', { index: index + 1 })}</strong>
        {onRemove && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={onRemove}>
            {t('app.cancel')}
          </button>
        )}
      </div>

      {line.productId === null ? (
        <div className="field">
          <span>{t('orders.productLabel')}</span>
          <input value={search} onChange={(e) => setSearch(e.target.value)} />
          {(options?.items ?? []).map((p) => (
            <button
              key={p.id}
              type="button"
              className="tree__row"
              onClick={() => {
                onChange({ productId: p.id, productLabel: `${p.name} (${p.sku})`, unitId: '' });
                setSearch('');
              }}
            >
              <span style={{ flex: 2 }}>{p.name}</span>
              <span style={{ width: '120px' }}>{p.sku}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'center' }}>
          <span>
            <strong>{line.productLabel}</strong>
          </span>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => onChange({ productId: null, productLabel: '', unitId: '' })}
          >
            {t('inbound.changeProduct')}
          </button>
        </div>
      )}

      {line.productId !== null && (
        <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'flex-end' }}>
          <label className="field" style={{ maxWidth: '140px' }}>
            <span>{t('inbound.stepQuantity')}</span>
            <input
              inputMode="decimal"
              value={line.qty}
              onChange={(e) => onChange({ qty: e.target.value })}
            />
          </label>
          <label className="field" style={{ maxWidth: '160px' }}>
            <span>{t('units.unit')}</span>
            <select value={line.unitId} onChange={(e) => onChange({ unitId: e.target.value })}>
              <option value="">{t('inbound.pickUnit')}</option>
              {activeUnits.map((u) => (
                <option key={u.uomId} value={u.uomId}>
                  {u.uomCode}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
    </div>
  );
}
